import { AnalyticsEventNameType, WebDashboardCustomThemeSummary } from "@coeff/api";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";

import { QueryCacheKey } from "../../api/queryClient";
import { useTrack } from "../../utils";

import { CUSTOM_THEME_ID_PREFIX, registerCustomThemeSpec, ThemeSpec } from "./themes";

interface Props {
  summary: WebDashboardCustomThemeSummary;
  /** Live-preview the theme on the dashboard as soon as the card appears (fresh replies only). */
  shouldAutoPreview: boolean;
  onPreviewTheme: (themePointer: string) => void;
  onApplyTheme: (themePointer: string) => void;
  onCancelPreview: () => void;
}

/**
 * Chat card for a `create_custom_theme` result: swatch strip + Apply / Keep current. Fresh replies
 * live-preview the theme immediately — Apply persists it, Keep current reverts the preview.
 */
export const ChatThemeResultCard: React.FC<Props> = ({
  summary,
  shouldAutoPreview,
  onPreviewTheme,
  onApplyTheme,
  onCancelPreview,
}) => {
  const themePointer = `${CUSTOM_THEME_ID_PREFIX}${summary.theme_id}`;
  const colors = summary.theme?.colors ?? [];

  const track = useTrack();
  const queryClient = useQueryClient();

  // "idle" = old card from history (no preview active); "previewing" = fresh reply being shown
  // live on the dashboard; then the user resolves it one way or the other.
  const [resolution, setResolution] = useState<"idle" | "previewing" | "applied" | "kept">(
    shouldAutoPreview ? "previewing" : "idle"
  );

  // Register the spec so `custom:` resolution works for preview/apply, then start the live
  // preview for fresh replies. Once per card instance.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    if (summary.theme_id && summary.theme) {
      registerCustomThemeSpec(themePointer, summary.theme as ThemeSpec);
    }
    // The theme was created outside the selector's fetch cycle; make its next open see it.
    queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_CUSTOM_THEMES]);
    if (shouldAutoPreview) {
      onPreviewTheme(themePointer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showActions = resolution === "previewing" || resolution === "idle";

  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-2 text-xs font-semibold text-gray-700">
        {summary.updated_existing ? "Custom theme updated" : "Custom theme created"}
      </div>
      <div className="flex h-7 gap-[2px] overflow-hidden rounded-lg">
        {colors.map((color, i) => (
          <div key={i} className="h-full flex-1" style={{ backgroundColor: color }} />
        ))}
      </div>
      {resolution === "previewing" && (
        <div className="mt-1.5 text-xs text-gray-500">Previewing on your dashboard now.</div>
      )}
      {resolution === "applied" && <div className="mt-1.5 text-xs text-gray-500">✓ Applied</div>}
      {resolution === "kept" && (
        <div className="mt-1.5 text-xs text-gray-500">
          Kept your current theme — it’s saved under “Custom palettes” in the theme selector.
        </div>
      )}
      {showActions && (
        <div className="mt-2.5 flex items-center gap-2">
          <button
            type="button"
            className="cursor-pointer rounded-md bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700"
            onClick={() => {
              if (resolution === "idle") {
                registerCustomThemeSpec(themePointer, summary.theme as ThemeSpec);
              }
              onApplyTheme(themePointer);
              setResolution("applied");
              track(AnalyticsEventNameType.VibeDashThemeChanged, {
                vibe_dash_theme_name: "custom_theme_agent_applied",
              });
            }}
          >
            Apply theme
          </button>
          {resolution === "previewing" ? (
            <button
              type="button"
              className="cursor-pointer rounded-md px-3 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100"
              onClick={() => {
                onCancelPreview();
                setResolution("kept");
              }}
            >
              Keep current
            </button>
          ) : (
            <button
              type="button"
              className="cursor-pointer rounded-md px-3 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100"
              onClick={() => {
                registerCustomThemeSpec(themePointer, summary.theme as ThemeSpec);
                onPreviewTheme(themePointer);
                setResolution("previewing");
              }}
            >
              Preview
            </button>
          )}
        </div>
      )}
    </div>
  );
};
